import { getUrlQueryParamValue } from 'utils/url.util';

/**
 * ==================
 * ORIGAMIA TEST MODE
 * ==================
 *
 * Lets Origamia content designers test an exercise outside of Tactileo + LTI.
 * The content URL given in the 'origamiaTestUrl' query parameter is played as an
 * LTI item with the Origamia theme, but without LTI launch, Tactileo authentication
 * nor Tactileo API calls. Only the AI feedback API is used (DEV environment).
 */

export const ORIGAMIA_TEST_URL_PARAM = 'origamiaTestUrl';
export const ORIGAMIA_TEST_ENV = 'DEV';
export const ORIGAMIA_TEST_SESSION_ID = 'origamia-test';
export const ORIGAMIA_TEST_ITEM_ID = 'origamia-test-item';

const ALLOWED_PROTOCOLS = ['http:', 'https:'];
const ALLOWED_HOSTNAMES = ['localhost'];

/**
 * Gets the Origamia test content URL from a query string.
 * @param {string} queryString.
 * @returns {URL|null} the content URL, or null if missing or not allowed.
 */
export const parseOrigamiaTestUrl = (queryString) => {
  const value = getUrlQueryParamValue(queryString, ORIGAMIA_TEST_URL_PARAM);
  if (!value) return null;

  let url;
  try {
    url = new URL(value);
  } catch {
    // eslint-disable-next-line no-console
    console.error(`[Origamia test mode] Invalid content URL: ${value}`);
    return null;
  }

  if (
    !ALLOWED_PROTOCOLS.includes(url.protocol) ||
    !ALLOWED_HOSTNAMES.includes(url.hostname)
  ) {
    // eslint-disable-next-line no-console
    console.error(
      `[Origamia test mode] Content URL not allowed: ${value} (allowed hosts: ${ALLOWED_HOSTNAMES.join(', ')})`,
    );
    return null;
  }

  return url;
};

// Read once at startup. The query string is kept on the player URL, so a reload stays in this mode.
const origamiaTestUrl = parseOrigamiaTestUrl(window.location.search);

export const isOrigamiaTestMode = () => origamiaTestUrl !== null;

export const getOrigamiaTestUrl = () => origamiaTestUrl?.href ?? null;

export const getOrigamiaTestOrigin = () => origamiaTestUrl?.origin ?? null;
